import { Box, Card, CardContent, FormLabel, Stack, Typography } from '@mui/joy';
import { WarningCircle } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import MarginBracket from 'assets/marginBracket.svg';
import { useAuthUser } from 'auth/hooks';
import { FormField } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { AllowedDecimalsTooltip, FlexBox, Image } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { getPercentage } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { useCostDecimals } from 'pricing/hooks';
import { externalSellPriceSourceFields } from 'quotes/constants';
import { ExternalPricingSourceTooltip, PriceLockTooltip, PriceSource } from 'quotes/elements';
import { PriceBasedOn } from 'quotes/enums';
import { getPurchaseCostLabel, pickPricingSourceFields } from 'quotes/utils';
import { Color, Direction, Level, Variant } from 'theme/enums';

import { usePriceFormFieldsContext } from './state';
import { UseLatestQuotePrice } from './UseLatestQuotePrice';
import { UseLatestSalesPrice } from './UseLatestSalesPrice';
import { ViewPreviousPrices } from './ViewPreviousPrices';

const MARGIN_SLOT_PROPS = {
  input: {
    max: 100,
    decimalScale: 2,
    allowNegative: true,
    suffix: '%',
  },
};

const MARGIN_BRACKET_STYLES = { pt: '45px', left: '-5px', zIndex: 1 };
const MARGIN_BRACKET_IMAGE = { height: 90, width: 14 };
const MARGIN_FIELD_CONTAINER = { width: '100%', ml: '16px' };
const MARGIN_FIELD_SX = { width: 'calc(50% - 12px)' };

interface IProps {
  isAlternateVersionEnabled: boolean;
}

export const PriceFormFields = ({ isAlternateVersionEnabled }: IProps) => {
  const { t } = useTranslation();

  const { control, errors, watch } = useFormContext();

  const {
    priceData,
    priceDecimals,
    disabledFields,
    standardCost,
    activeCost,
    priceBasedOn,
    marginFieldName,
    onChangePrice,
    onChangeMargin,
    onPriceKeyDown,
  } = usePriceFormFieldsContext();

  const { user } = useAuthUser();
  const { minimumMarginThreshold, minMarginWarningThreshold, maxMarginWarningThreshold } =
    user?.companySettings ?? {};
  const costDecimals = useCostDecimals();

  const isReplacementMode = priceBasedOn === PriceBasedOn.REPLACEMENT;

  const activeMarginValue = isReplacementMode ? watch('pricingMargin') : watch(marginFieldName);

  const marginWarning = (key: string, message: string) => (
    <Typography
      key={key}
      level={Level.BODY_XS}
      color={Color.WARNING}
      startDecorator={<WarningCircle />}
    >
      {message}
    </Typography>
  );

  const marginHelperTexts = useMemo(() => {
    if (activeMarginValue == null) {
      return [];
    }

    const margin = Number(activeMarginValue) / 100;

    const gpmMin = Number(minMarginWarningThreshold);
    const gpmMax = Number(maxMarginWarningThreshold);

    if (gpmMin && gpmMax && (margin < gpmMin || margin > gpmMax)) {
      return [marginWarning('gpmOutOfRange', t('warningValidationGpm'))];
    }

    const minThreshold = Number(minimumMarginThreshold);

    if (minThreshold && margin < minThreshold) {
      return [
        marginWarning(
          'marginBelowThreshold',
          t('warningMarginBelowMinimumThreshold', {
            minimum: getPercentage(minThreshold),
          })
        ),
      ];
    }

    return [];
  }, [
    activeMarginValue,
    minMarginWarningThreshold,
    maxMarginWarningThreshold,
    minimumMarginThreshold,
    t,
  ]);

  const formatSubtitleCost = (amount: number) =>
    PricingHelper.formatPricing({
      amount,
      decimals: costDecimals,
      fixedDecimals: true,
    });

  const subtitle = isReplacementMode
    ? t('messagePriceBasedOnReplacementCost', {
        cost: activeCost ? formatSubtitleCost(activeCost) : DEFAULT_PLACEHOLDER,
      })
    : t('messagePriceBasedOnPurchaseCost', {
        cost: standardCost ? formatSubtitleCost(standardCost) : DEFAULT_PLACEHOLDER,
        costLabel: getPurchaseCostLabel(isAlternateVersionEnabled),
      });

  const marginFields = isReplacementMode ? (
    <Stack direction={Direction.ROW} sx={{ position: 'relative' }}>
      <Box sx={{ position: 'absolute', ...MARGIN_BRACKET_STYLES }}>
        <Image src={MarginBracket} style={MARGIN_BRACKET_IMAGE} />
      </Box>
      <Box sx={MARGIN_FIELD_CONTAINER}>
        <Stack gap={2}>
          <FormField
            name={marginFieldName}
            type={InputType.NUMBER_INPUT}
            inputProps={{
              control,
              error: errors?.[marginFieldName],
              label: (
                <>
                  {t('labelTrueMargin')}
                  <Typography color={Color.NEUTRAL} level={Level.BODY_XS} ml={0.5}>
                    ({t('labelBasedOnCost', { cost: t('labelQuotedCost') })})
                  </Typography>
                </>
              ),
              placeholder: t('placeholderUpsertLineMargin'),
              disabled: true,
              slotProps: MARGIN_SLOT_PROPS,
              sx: MARGIN_FIELD_SX,
            }}
          />

          <FormField
            name="pricingMargin"
            type={InputType.NUMBER_INPUT}
            inputProps={{
              control,
              error: errors?.pricingMargin,
              label: (
                <>
                  {t('labelPricingMargin')}
                  <Typography color={Color.NEUTRAL} level={Level.BODY_XS} ml={0.5}>
                    ({t('labelBasedOnCost', { cost: t('labelReplacementCost') })})
                  </Typography>
                </>
              ),
              placeholder: t('placeholderUpsertLineMargin'),
              disabled: disabledFields,
              onChange: onChangeMargin,
              onKeyDown: onPriceKeyDown,
              slotProps: MARGIN_SLOT_PROPS,
              sx: MARGIN_FIELD_SX,
              helperTexts: marginHelperTexts,
            }}
          />
        </Stack>
      </Box>
    </Stack>
  ) : (
    <Box sx={MARGIN_FIELD_CONTAINER}>
      <FormField
        name={marginFieldName}
        type={InputType.NUMBER_INPUT}
        inputProps={{
          control,
          error: errors?.[marginFieldName],
          label: t('labelPricingMargin'),
          placeholder: t('placeholderUpsertLineMargin'),
          disabled: disabledFields,
          onChange: onChangeMargin,
          onKeyDown: onPriceKeyDown,
          slotProps: MARGIN_SLOT_PROPS,
          sx: MARGIN_FIELD_SX,
          helperTexts: marginHelperTexts,
        }}
      />
    </Box>
  );

  return (
    <>
      <Card
        variant={Variant.OUTLINED}
        color={Color.PRIMARY}
        sx={{ backgroundColor: 'neutral.100' }}
      >
        <CardContent>
          <Stack gap={2}>
            <Stack gap={0.5}>
              <Typography level={Level.TITLE_MD}>{t('titlePrice')}</Typography>
              <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.600' }}>
                {subtitle}
              </Typography>
            </Stack>

            {marginFields}

            <Stack direction={Direction.ROW} gap={2}>
              <Box sx={{ width: '50%' }}>
                <FormField
                  name="price"
                  type={InputType.NUMBER_INPUT}
                  inputProps={{
                    control,
                    error: errors?.price,
                    label: (
                      <FlexBox>
                        {t('labelPrice')}
                        <PriceLockTooltip priceLocked={priceData.priceLocked} />
                      </FlexBox>
                    ),
                    placeholder: t('placeholderUpsertLinePrice'),
                    disabled: disabledFields,
                    onChange: onChangePrice,
                    onKeyDown: onPriceKeyDown,
                    endDecorator: <AllowedDecimalsTooltip decimals={priceDecimals} />,
                    slotProps: {
                      input: {
                        prefix: '$',
                        thousandSeparator: true,
                        decimalScale: priceDecimals,
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ width: '50%' }}>
                <ExternalPricingSourceTooltip
                  pricingData={pickPricingSourceFields(priceData, externalSellPriceSourceFields)}
                  isAlternateVersionEnabled={isAlternateVersionEnabled}
                  color={Color.SUCCESS}
                >
                  <Box key={priceData.priceSourceType} gap={0.5}>
                    <FormLabel>{t('labelPriceSource')}</FormLabel>
                    <Box sx={{ minHeight: 32, display: 'flex', alignItems: 'center' }}>
                      <PriceSource priceSource={priceData} />
                    </Box>
                  </Box>
                </ExternalPricingSourceTooltip>
              </Box>
            </Stack>

            <Typography color={Color.WARNING} level={Level.TITLE_SM}>
              {t('messageChangePriceHint')}
            </Typography>

            {isAlternateVersionEnabled ? (
              <UseLatestSalesPrice disabled={disabledFields} />
            ) : (
              <UseLatestQuotePrice disabled={disabledFields} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {!disabledFields && <ViewPreviousPrices />}
    </>
  );
};
